import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Table, Input, TransitionablePortal, Confirm } from "semantic-ui-react";
import { toast } from "../../..";
import { ITask } from "../../../models/task";
import {
    deleteCalculation,
    downloadF310,
    downloadF427,
    stopCalculation,
} from "../../../services/calculationsService";
import { AppState } from "../../../store/configureStore";
import SubtasksStatus from "../../SubtasksStatus/SubtasksStatus";
import { formatDateString } from "./Logs";
import { LtccType, ValidationProcess } from "../../../store/validationProcess";

const getCalculationType = (type: number) => {
    switch (type) {
        case 0:
            return "Perform security assessment";
        case 1:
            return "Estimate security reserve";
        case 2:
            return "F427 conversion";
        default:
            return "";
    }
};

const formatFinishedDateString = (log: ITask) => {
    if (log.finished === "0001-01-01T00:00:00")
        return (
            <span className="finished-loading-message">
                Calculating<span className="one">.</span>
                <span className="two">.</span>
                <span className="three">.</span>
            </span>
        );
    else if (log.hasStoppedSubtask) {
        return <span>Manually stopped</span>;
    }
    else if (log.isFailed && !log.hasSuccessfulSubtask) return <span>Process failed</span>;
    else {
        let result = log.finished.split(".")[0].replace("T", " ");
        if (log.hasFailedSubtask) result += " ERR-I";
        else if (log.hasFallbackSubtask) result += " WRN-F";
        return <span>{result}</span>;
    }
};

interface IProps {
    log: ITask;
}

export const LogRow = ({ log }: IProps) => {
    const user = useSelector((state: AppState) => state.user);
    const { justification } = useSelector((state: AppState) => state.raoReport);
    const [f310Version, setF310Version] = useState<string | number>(1);
    const [f427Version, setF427Version] = useState<string | number>(1);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [stopModalOpen, setStopModalOpen] = useState(false);
    const downloadable = log.finished !== "0001-01-01T00:00:00" && log.calculationType === 1;

    const status = useMemo(() => {
        if (!log || !log.applicationName) return null;
        let parts = log.applicationName.split("/");
        return parts.length === 4 ? parts.map(n => Number.parseInt(n)) : null;
    }, [log]);

    const handleDownloadF310 = async () => {
        try {
            await downloadF310(log.guid, +f310Version, justification, log.validationProcess, log.ltccType);
        } catch (error) {
            console.error(error);
            toast("Unable to download file, please try again", false);
        }
    };

    const handleDownloadF427 = async () => {
        try {
            await downloadF427(log.guid, +f427Version);
        } catch (error) {
            console.error(error);
            toast("Unable to download file, please try again", false);
        }
    };

    const handleDeleteCalculation = async () => {
        const res = await deleteCalculation(log.guid);
        if (res.ok) toast("Calculation deleted", true);
        else toast("Error while deleting calculation", false);
        setDeleteModalOpen(false);
    };

    const handleStopCalculation = async () => {
        const res = await stopCalculation(log.guid);
        if (res.ok) toast("Calculation will be stopped", true);
        else toast("Error while stopping calculation", false);
        setStopModalOpen(false);
    };

    let validationProcessString = "Unknown";
    let partialDownloadFileString = "";
    if (log.validationProcess === ValidationProcess.DayAhead) {
        validationProcessString = "DA";
        partialDownloadFileString = "310";
    }
    else if (log.validationProcess === ValidationProcess.IntraDay) {
        validationProcessString = "ID";
        partialDownloadFileString = "710";
    }
    else if (log.validationProcess === ValidationProcess.Ltcc && log.ltccType === LtccType.Merged) {
        validationProcessString = "LT";
        partialDownloadFileString = "401";
    }
    else if (log.validationProcess === ValidationProcess.Ltcc && log.ltccType === LtccType.PerTimestamp) {
        validationProcessString = "LT";
        partialDownloadFileString = "310";
    }

    return (
        <>
            <Table.Row id={`table-row-logrow-${log.id}`} key={log.id}>
                <Table.Cell id={`table-cell-logrow-a-${log.id}`} collapsing>{log.id}</Table.Cell>
                <Table.Cell id={`table-cell-logrow-b-${log.id}`} collapsing>{formatDateString(log.calculationDate!, false)}</Table.Cell>
                <Table.Cell id={`table-cell-logrow-c-${log.id}`} collapsing>{log.user!}</Table.Cell>
                <Table.Cell id={`table-cell-logrow-d-${log.id}`} collapsing>{formatDateString(log.started)}</Table.Cell>
                <Table.Cell id={`table-cell-logrow-e-${log.id}`} collapsing>{formatFinishedDateString(log)}</Table.Cell>
                <Table.Cell id={`table-cell-logrow-f-${log.id}`} collapsing>{validationProcessString}</Table.Cell>
                <Table.Cell id={`table-cell-logrow-g-${log.id}`} >{getCalculationType(log.calculationType!)}</Table.Cell>
                <Table.Cell id={`table-cell-logrow-h-${log.id}`}>
                    {status && (
                        <SubtasksStatus
                            failed={status[0]}
                            success={status[1]}
                            running={status[2]}
                            total={status[3]}
                        />
                    )}
                </Table.Cell>
                <Table.Cell id={`table-cell-logrow-i-${log.id}`} textAlign="right">
                    <Link id={`link-logrow-a-${log.id}`} className="logs-action" to={`/Calculations/${log.guid}/Tasks`}>
                        Status
                    </Link>
                    {log.calculationType !== 2 && (
                        <>
                            <span id={`span-logrow-a-${log.id}`} className="cell-divider"></span>
                            <Link id={`link-logrow-b-${log.id}`} className="logs-action" to={`/Calculations/${log.guid}`}>
                                Properties
                            </Link>
                        </>
                    )}
                    {downloadable && log.calculationType !== 2 && log.hasSuccessfulFirstStageTasks && (
                        <>
                            <span id={`span-logrow-b-${log.id}`} className="cell-divider"></span>

                            <span id={`span-logrow-c-${log.id}`} className="logs-version">
                                {!log.isComplete && "Partial"} F{partialDownloadFileString} V
                                <Input
                                    id={`input-logRow-a-${log.id}`}
                                    type="tel"
                                    value={f310Version}
                                    pattern="^-?[0-9]\d*\.?\d*$"
                                    onChange={e => setF310Version(e.target.value)}
                                />
                            </span>
                            <span
                                className={`logs-action ${f310Version as number <= 0 ? "disabled" : ""}`}
                                id={`span-logrow-d-${log.id}`}
                                onClick={handleDownloadF310}
                            >
                                Download
                            </span>
                        </>
                    )}
                    {log.hasF427 && (
                        <>
                            <span id={`span-logrow-e-${log.id}`} className="cell-divider"></span>
                            <span id={`span-logrow-f-${log.id}`} className="logs-version">
                                F427 V
                                <Input
                                    id={`input-logRow-b-${log.id}`}
                                    type="tel"
                                    value={f427Version || ""}
                                    pattern="^-?[0-9]\d*\.?\d*$"
                                    onChange={e => setF427Version(e.target.value)}
                                />
                            </span>
                            <span
                                className={`logs-action ${f427Version as number <= 0 ? "disabled" : ""}`}
                                id={`span-logrow-g-${log.id}`}
                                onClick={handleDownloadF427}
                            >
                                Download
                            </span>
                        </>
                    )}
                    {log.finished === "0001-01-01T00:00:00" && (
                        <>
                            <span id={`span-logrow-h-${log.id}`} className="cell-divider"></span>
                            <span id={`span-logrow-i-${log.id}`} className="logs-action" onClick={() => setStopModalOpen(true)}>
                                Stop
                            </span>
                        </>
                    )}
                    {(user.role === "Admin" || user.role === "SuperAdmin") && (
                        <>
                            <span id={`span-logrow-j-${log.id}`} className="cell-divider"></span>
                            <span id={`span-logrow-k-${log.id}`} className="logs-action" onClick={() => setDeleteModalOpen(true)}>
                                Delete
                            </span>
                        </>
                    )}
                </Table.Cell>
            </Table.Row>
            <TransitionablePortal
                id={`trnasitionablePortal-logRow-a-${log.id}`}
                open={deleteModalOpen}
                onOpen={() => setTimeout(() => document.body.classList.add("modal-fade-in"), 0)}
                transition={{ animation: "scale", duration: 300 }}
            >
                <Confirm
                    id={`confirm-logRow-a-${log.id}`}
                    size="mini"
                    open={deleteModalOpen}
                    onCancel={() => setDeleteModalOpen(false)}
                    onConfirm={() => handleDeleteCalculation()}
                />
            </TransitionablePortal>
            <TransitionablePortal
                id={`trnasitionablePortal-logRow-b-${log.id}`}
                open={stopModalOpen}
                onOpen={() => setTimeout(() => document.body.classList.add("modal-fade-in"), 0)}
                transition={{ animation: "scale", duration: 300 }}
            >
                <Confirm
                    id={`confirm-logRow-b-${log.id}`}
                    size="mini"
                    open={stopModalOpen}
                    onCancel={() => setStopModalOpen(false)}
                    onConfirm={() => handleStopCalculation()}
                />
            </TransitionablePortal>
        </>
    );
};
